import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Modal, SimpleTable } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { navigation } from '@/navigation';
import { daysOverdue, useCardDate } from './InvoiceCardPart';
import {
  isValidInvoiceCardId,
  toInvoiceCardVariant,
  type InvoiceCollectionPartData,
  type InvoiceCardVariant,
  type InvoiceRowData
} from './invoice-card.types';
import { useInvoiceCardStatus } from './use-invoice-card-status';
import { CardActions, CardButton, ResultCard, ResultCardHeader } from '../ResultCard';
import { useFormatCurrency } from '@/hooks/use-format-currency';

interface Props {
  part: InvoiceCollectionPartData;
}

/** Column set of the full-list table — the variant decides the last, most relevant column. */
function useHeaders(variant: InvoiceCardVariant): string[] {
  const t = useFeatureTranslation('aiChat');
  const trailing =
    variant === 'validation'
      ? t('invoiceCollection.issues')
      : variant === 'payment'
        ? t('invoiceCollection.dueDate')
        : t('invoiceCollection.status');
  return [
    t('invoiceCollection.number'),
    t('invoiceCollection.supplier'),
    t('invoiceCollection.amount'),
    trailing
  ];
}

/** Compact "3 / 2" issue summary — a dash when the row is clean. */
function IssueCell({ row }: { row: InvoiceRowData }) {
  const errors = row.errorCount ?? 0;
  const warnings = row.warningCount ?? 0;
  if (errors === 0 && warnings === 0) return <span className='text-foreground-muted'>—</span>;
  return (
    <span className='flex items-center gap-xs'>
      {errors > 0 && (
        <Badge color='error' size='sm'>
          {errors}
        </Badge>
      )}
      {warnings > 0 && (
        <Badge color='warning' size='sm'>
          {warnings}
        </Badge>
      )}
    </span>
  );
}

/**
 * One card standing in for a whole invoice result. Aggregates are computed server-side over
 * everything that was read, so the headline numbers stay true even when the embedded row
 * list is shorter — the card says so rather than truncating silently.
 */
export function InvoiceCollectionPart({ part }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const t = useFeatureTranslation('aiChat');
  const statusOf = useInvoiceCardStatus();
  const formatAmount = useFormatCurrency();
  const formatDate = useCardDate();
  const variant = toInvoiceCardVariant(part.variant);
  const headers = useHeaders(variant);

  const errors = part.errorCount ?? 0;
  const warnings = part.warningCount ?? 0;
  // The row list can be shorter than what the aggregates cover — never hide that.
  const isPartialList = part.shown < part.total;

  const trailingCell = (row: InvoiceRowData) => {
    if (variant === 'validation') return <IssueCell row={row} />;
    if (variant === 'payment') {
      const overdue = daysOverdue(row.dueDate);
      return (
        <span className={overdue !== null && overdue > 0 ? 'text-error-text' : undefined}>
          {formatDate(row.dueDate) ?? '—'}
        </span>
      );
    }
    const status = statusOf(row);
    return status ? (
      <Badge color={status.color} size='sm'>
        {status.label}
      </Badge>
    ) : (
      <span className='text-foreground-muted'>—</span>
    );
  };

  return (
    <ResultCard>
      <ResultCardHeader title={t('invoiceCollection.title', { count: part.total })}>
        {variant === 'validation' && (errors > 0 || warnings > 0) && (
          <span className='flex items-center gap-xs'>
            {errors > 0 && (
              <Badge color='error' size='sm'>
                {t('invoiceCard.errorCount', { count: errors })}
              </Badge>
            )}
            {warnings > 0 && (
              <Badge color='warning' size='sm'>
                {t('invoiceCard.warningCount', { count: warnings })}
              </Badge>
            )}
          </span>
        )}
      </ResultCardHeader>

      {/* One row per currency — money is never summed across currencies. */}
      <div className='flex flex-col gap-2xs'>
        {part.totalsByCurrency.map(total => (
          <div key={total.currency} className='flex items-baseline justify-between gap-sm'>
            <span className='text-foreground-muted text-sm'>
              {t('invoiceCollection.currencyCount', {
                count: total.count,
                currency: total.currency
              })}
            </span>
            <span className='font-semibold text-foreground-base'>
              {formatAmount(total.total, total.currency)}
            </span>
          </div>
        ))}
      </div>

      <CardActions>
        <CardButton onClick={() => setIsOpen(true)}>
          {t('invoiceCollection.showAll', { count: part.shown })}
        </CardButton>
      </CardActions>

      <Modal open={isOpen} onClose={() => setIsOpen(false)} size='lg'>
        <Modal.Header
          onClose={() => setIsOpen(false)}
          label={t('invoiceCollection.title', { count: part.total })}
          description={
            isPartialList
              ? t('invoiceCollection.partialNotice', { shown: part.shown, total: part.total })
              : undefined
          }
        />
        <Modal.Content>
          <SimpleTable
            headers={headers}
            data={part.invoices}
            scrollable
            onRowClick={row =>
              isValidInvoiceCardId(row.id) && navigate(navigation.invoiceDetail(row.id))
            }
            renderRow={row => [
              <span key='n' className='font-medium'>
                {row.invoiceNumber ?? '—'}
              </span>,
              <span key='s'>{row.supplier ?? '—'}</span>,
              <span key='a'>{formatAmount(row.amount, row.currency) ?? '—'}</span>,
              <span key='t'>{trailingCell(row)}</span>
            ]}
          />
        </Modal.Content>
      </Modal>
    </ResultCard>
  );
}
